import { useEffect, useMemo, useState } from 'react';
import { api, scr } from '../lib/api';
import { mdaName, pct } from '../lib/types';
import type { AdoptionResult, RevenueResult, SlaComplianceResult, VolumesResult } from '../lib/types';
import { EmptyState, ErrorNote, PageHeader, Spinner } from '../ui/components';

interface LeagueRow {
  mdaCode: string;
  compliancePct: number | null;
  totalClosed: number;
  breached: number;
  serviceVolume: number;
  revenueScr: number;
  caseDigitalPct: number | null;
  usageDigitalPct: number | null;
}

type SortKey = 'mda' | 'compliance' | 'volume' | 'revenue' | 'caseDigital' | 'usageDigital';

/**
 * c14-analytics has no /dims endpoint listing every MDA, so the roster this
 * table ranks is derived — same technique govfin-portal's FinanceOverview
 * uses to derive its vote list from combinations — from the union of
 * mdaCodes appearing in the three unfiltered KPI calls below. Per-MDA
 * digital-adoption columns reuse GET /kpis/adoption's own `?mda=` filter
 * (one call per known MDA); there is no per-*service* breakdown on that
 * endpoint (adoption() takes no serviceCode param), so this table — not a
 * separate "adoption tracker" page — is this build's home for the spec's
 * adoption-tracking ask, at the granularity the backend actually supports.
 */
export function LeagueTable() {
  const [rows, setRows] = useState<LeagueRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('compliance');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [sla, volumes, revenue] = await Promise.all([
          api.get<SlaComplianceResult>('/api/v1/analytics/kpis/sla-compliance'),
          api.get<VolumesResult>('/api/v1/analytics/kpis/volumes'),
          api.get<RevenueResult>('/api/v1/analytics/kpis/revenue'),
        ]);
        const mdaCodes = Array.from(
          new Set([
            ...(sla.byMda ?? []).map((m) => m.mdaCode),
            ...(volumes.byMda ?? []).map((m) => m.mdaCode),
            ...(revenue.byMda ?? []).map((m) => m.mdaCode),
          ]),
        ).sort();

        const adoptions = await Promise.all(
          mdaCodes.map((code) =>
            api
              .get<AdoptionResult>(`/api/v1/analytics/kpis/adoption?mda=${encodeURIComponent(code)}`)
              .catch(() => null),
          ),
        );

        const slaByMda = new Map((sla.byMda ?? []).map((m) => [m.mdaCode, m]));
        const volByMda = new Map((volumes.byMda ?? []).map((m) => [m.mdaCode, m]));
        const revByMda = new Map<string, number>();
        for (const r of revenue.byMda ?? []) {
          revByMda.set(r.mdaCode, (revByMda.get(r.mdaCode) ?? 0) + r.amount);
        }

        const built: LeagueRow[] = mdaCodes.map((code, i) => ({
          mdaCode: code,
          compliancePct: slaByMda.get(code)?.compliancePct ?? null,
          totalClosed: slaByMda.get(code)?.totalClosed ?? 0,
          breached: slaByMda.get(code)?.breached ?? 0,
          serviceVolume: volByMda.get(code)?.count ?? 0,
          revenueScr: revByMda.get(code) ?? 0,
          caseDigitalPct: adoptions[i]?.cases.digitalRatioPct ?? null,
          usageDigitalPct: adoptions[i]?.serviceUsage.digitalRatioPct ?? null,
        }));

        if (alive) setRows(built);
      } catch {
        if (alive) setError('Could not load the MDA league table');
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const sorted = useMemo(() => {
    if (!rows) return null;
    const dir = sortDir === 'asc' ? 1 : -1;
    const val = (r: LeagueRow): number | string => {
      switch (sortKey) {
        case 'mda':
          return mdaName(r.mdaCode);
        case 'compliance':
          return r.compliancePct ?? -1;
        case 'volume':
          return r.serviceVolume;
        case 'revenue':
          return r.revenueScr;
        case 'caseDigital':
          return r.caseDigitalPct ?? -1;
        case 'usageDigital':
          return r.usageDigitalPct ?? -1;
      }
    };
    return [...rows].sort((a, b) => {
      const av = val(a);
      const bv = val(b);
      if (typeof av === 'string' || typeof bv === 'string') return String(av).localeCompare(String(bv)) * dir;
      return (av - bv) * dir;
    });
  }, [rows, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const Th = ({ label, k }: { label: string; k: SortKey }) => (
    <th className="table-head cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort(k)}>
      {label} {sortKey === k ? (sortDir === 'asc' ? '▲' : '▼') : ''}
    </th>
  );

  return (
    <div>
      <PageHeader
        title="MDA performance league table"
        subtitle="Ranking every reporting MDA on SLA compliance, service volume, revenue and digital adoption — click a column to sort"
      />
      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="card overflow-x-auto">
        {rows === null ? (
          <Spinner />
        ) : sorted!.length === 0 ? (
          <EmptyState
            title="No MDA activity recorded yet"
            hint="Once cases, payments or service-usage facts are ingested, ministries will rank here."
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <Th label="MDA" k="mda" />
                <Th label="SLA compliance" k="compliance" />
                <th className="table-head text-right">Cases closed</th>
                <th className="table-head text-right">Breached</th>
                <Th label="Service volume" k="volume" />
                <Th label="Revenue" k="revenue" />
                <Th label="Case digital %" k="caseDigital" />
                <Th label="Usage digital %" k="usageDigital" />
              </tr>
            </thead>
            <tbody>
              {sorted!.map((r) => (
                <tr key={r.mdaCode} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">
                    {mdaName(r.mdaCode)}
                    <div className="text-xs text-slate-400">{r.mdaCode}</div>
                  </td>
                  <td className="table-cell text-right tabular-nums">
                    <span
                      className={
                        r.compliancePct == null
                          ? 'text-slate-400'
                          : r.compliancePct >= 90
                            ? 'font-semibold text-seagreen-600'
                            : r.compliancePct >= 75
                              ? 'text-amber-600'
                              : 'font-semibold text-rose-600'
                      }
                    >
                      {pct(r.compliancePct)}
                    </span>
                  </td>
                  <td className="table-cell text-right tabular-nums">{r.totalClosed.toLocaleString()}</td>
                  <td className="table-cell text-right tabular-nums">{r.breached.toLocaleString()}</td>
                  <td className="table-cell text-right tabular-nums">{r.serviceVolume.toLocaleString()}</td>
                  <td className="table-cell text-right tabular-nums">{scr(r.revenueScr)}</td>
                  <td className="table-cell text-right tabular-nums">{pct(r.caseDigitalPct)}</td>
                  <td className="table-cell text-right tabular-nums">{pct(r.usageDigitalPct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
